import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { Checkbox } from "@ui/external/components/checkbox";
import MirrorEditor from "@ui/external/components/codeeditor";
import LabelSwitch from "@ui/external/components/LabelSwitch";
import PhonePicker from "@ui/external/components/phonepicker";
import PrefixInput from "@ui/external/components/prefixinput";
import TextEditor, { RichTextEditor } from "@ui/external/components/texteditor";
import {
  AsyncSelect,
  DndUpload,
  OTPInput,
} from "@ui/internal/components/formelement";
import { ErrorLabel, FormLabel } from "@ui/internal/components/label";
import parsePhoneNumberFromString from "libphonenumber-js/min";
import { useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import CurrencyInput from "@ui/external/components/selectcurrency";
import CreatableSelect from "react-select/creatable";

import ReactSelect from "react-select";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Maximize2 } from "lucide-react";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@components/ui/resizable";
import UploadToCloud from "@ui/internal/components/filetocloud";
import { Calendar22 } from "@ui/internal/components/date";
import FileUpload from "@ui/internal/components/file-upload/FileUpload";

import EditorJSComponent from "@ui/external/components/editorjs";

const isJson = (val: any) => {
  if (!val) return false;
  if (typeof val === "object") {
    return val.blocks || val.time;
  }
  if (typeof val !== "string") return false;
  try {
    const parsed = JSON.parse(val);
    return (
      typeof parsed === "object" &&
      parsed !== null &&
      (parsed.blocks || parsed.time)
    );
  } catch (e) {
    return false;
  }
};

const EditorJSField = ({ field, props, name, errors }: any) => {
  const [isMigrationMode, setIsMigrationMode] = useState(false);
  const [initialHtml, setInitialHtml] = useState<string | null>(null);

  useEffect(() => {
    // Detect if we're starting with non-JSON content (migration)
    // We watch field.value because the form might reset late after an API fetch
    if (field.value && !isJson(field.value) && !isMigrationMode) {
      setIsMigrationMode(true);
      setInitialHtml(field.value);
    }
  }, [field.value, isMigrationMode]);

  const isDataJson = field.value && isJson(field.value);

  return (
    <div className="flex flex-col gap-2">
      {props.label && <FormLabel label={props.label} />}

      {isMigrationMode && (
        <>
          <div className="bg-amber-100 border border-amber-300 p-3 rounded-md text-sm text-amber-800 mb-4 shadow-sm animate-in fade-in duration-500">
            <strong>Migration Mode:</strong> We've detected old HTML content.
            Please copy the content from the <strong>Classic Editor</strong>{" "}
            below and paste it into the <strong>EditorJS</strong> instance.{" "}
            <br />
          </div>
          <div className="border border-amber-200 rounded-md p-4 bg-slate-50/50 mb-4">
            <h4 className="text-xs font-bold text-slate-500 uppercase mb-2 tracking-wider">
              Classic Editor (Source)
            </h4>
            <TextEditor
              {...props}
              height={props.height || 400}
              name={`${name}-migration-source`}
              initialValue={initialHtml || field.value}
            />
          </div>
        </>
      )}

      <div
        className={
          isMigrationMode
            ? "border border-blue-200 rounded-md p-4 bg-blue-50/20"
            : ""
        }
      >
        {isMigrationMode && (
          <h4 className="text-xs font-bold text-blue-500 uppercase mb-2 tracking-wider">
            EditorJS (Destination)
          </h4>
        )}

        {/* Render TextEditor ONLY if we are NOT in migration mode but have legacy HTML data */}
        {!isMigrationMode && !isDataJson && field.value && (
          <div className="mb-4">
            <TextEditor
              {...props}
              {...field}
              height={props.height || 400}
              name={name}
              initialValue={field.value}
              error={errors[name] && errors[name].message}
            />
          </div>
        )}

        {/* This component is now ALWAYS at this location in the DOM tree, preventing flickers on paste */}
        <EditorJSComponent
          {...props}
          {...field}
          placeholder={props.placeholder}
          height={props.height || 400}
        />
      </div>
      {name && errors[name] && <ErrorLabel message={errors[name].message} />}
    </div>
  );
};

/* Certificate templates are complete documents: their <head> carries the
   @font-face and layout rules the design depends on. Injecting one with
   dangerouslySetInnerHTML drops everything outside <body>, so the template
   loses its fonts and sizing. An iframe gives it a real document instead —
   the same reason printCertificates() renders into one. */
const TemplateFrame = ({
  html,
  title,
  className,
}: {
  html: string;
  title: string;
  className?: string;
}) => {
  const [height, setHeight] = useState(0);
  const frameRef = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    const frame = frameRef.current;
    const doc = frame?.contentWindow?.document;
    if (!frame || !doc) return;

    doc.open();
    doc.write(html);
    doc.close();

    /* The frame is resized to fit its document, so it never needs to scroll
       itself — but a stored template can still leave the document scrollable
       and the resulting scrollbar shows as a strip beside the sheet. Templates
       come from the API, so this has to be enforced here rather than in any one
       template's markup. */
    const noScroll = doc.createElement("style");
    noScroll.textContent =
      "html,body{overflow:hidden!important;scrollbar-width:none}" +
      "html::-webkit-scrollbar,body::-webkit-scrollbar{width:0;height:0;display:none}";
    (doc.head || doc.documentElement)?.appendChild(noScroll);

    /* Size the frame to its content so the surrounding dialog does the
       scrolling. `overflow: hidden` above makes scrollHeight report only the
       viewport, so measure the real content instead: the union of the
       top-level elements' boxes, with scrollHeight as a last resort. */
    const measure = () => {
      const body = doc.body;
      if (!body) return;

      let content = 0;
      Array.from(body.children).forEach((node) => {
        const el = node as HTMLElement;
        const rect = el.getBoundingClientRect();
        const marginBottom = parseFloat(
          doc.defaultView?.getComputedStyle(el).marginBottom || "0"
        );
        content = Math.max(content, rect.bottom + (marginBottom || 0));
      });

      const next = Math.max(content, body.scrollHeight);
      if (next) setHeight(Math.ceil(next));
    };

    measure();
    /* fonts and images settle after the first paint and change the height */
    const timers = [80, 300, 900, 2000].map((ms) =>
      window.setTimeout(measure, ms)
    );
    (doc as any).fonts?.ready?.then?.(measure);

    return () => timers.forEach(window.clearTimeout);
  }, [html]);

  return (
    <iframe
      ref={frameRef}
      title={title}
      /* the template is authored by admins of this app, but it is still
         data — keep it from navigating or scripting the parent */
      sandbox="allow-same-origin"
      className={className}
      style={height ? { height } : undefined}
    />
  );
};

const CodeMirrorField = ({ field, props, name, errors }: any) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const html = field.value || "";

  return (
    <>
      <FormLabel label={props.label} />
      <div
        className={` h-[500px] overflow-hidden border shadow-sm rounded-md ${
          errors[name] &&
          "border border-red-500 outline-[3px] outline-destructive/20"
        } gap-[10px]`}
      >
        {/* This is the Code editor with ResizablePanel */}
        <ResizablePanelGroup direction="horizontal">
          <ResizablePanel>
            <h2 className="w-full h-[60px] mb-4 font-semibold flex items-center justify-start ps-3 bg-[#f6f6f6] text-center">
              index.html
            </h2>
            <MirrorEditor
              value={field.value}
              onChange={field.onChange}
              error={errors[name] && errors[name].message}
            />
          </ResizablePanel>
          <ResizableHandle />
          <ResizablePanel>
            <div className="h-full flex flex-col overflow-hidden">
              <h2 className="w-full h-[60px] shrink-0 mb-4 font-semibold flex items-center justify-center gap-2 bg-[#f6f6f6] text-center">
                Preview
                <button
                  type="button"
                  onClick={() => html && setIsFullscreen(true)}
                  className="inline-flex items-center gap-1 text-[12px] font-medium text-muted-foreground hover:text-foreground border rounded-md px-2 py-1 bg-white cursor-pointer"
                  title="Open fullscreen preview"
                >
                  <Maximize2 className="size-3.5" />
                  Expand
                </button>
              </h2>
              <div
                onClick={() => html && setIsFullscreen(true)}
                title={html ? "Click to view fullscreen" : undefined}
                className={`flex-1 overflow-auto group relative bg-white ${
                  html ? "cursor-zoom-in" : ""
                }`}
              >
                {html ? (
                  <>
                    <TemplateFrame
                      html={html}
                      title="Template preview"
                      className="w-full border-0 pointer-events-none block"
                    />
                    <div className="sticky bottom-3 flex justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      <span className="inline-flex items-center gap-1.5 text-[12px] font-medium bg-black/70 text-white rounded-full px-3 py-1.5">
                        <Maximize2 className="size-3.5" />
                        Click to view fullscreen
                      </span>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground text-center pt-10">
                    Nothing to preview yet
                  </p>
                )}
              </div>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
      <span className="text-[12px] text-red-600">
        {errors[name] && errors[name].message}
      </span>

      <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
        {/* the shared DialogContent puts its close button at -16px outside the
            panel, so overflow-hidden here would clip it — the inner scroll
            container below is what needs to clip instead */}
        {/* flex-col with a fixed height, not the default content-sized grid:
            a grid track still grows to fit the certificate, so the scroll
            container never overflows and never shows a scrollbar */}
        <DialogContent className="w-[95vw] max-w-[95vw] sm:max-w-[95vw] h-[92vh] p-0 gap-0 flex flex-col">
          <DialogHeader className="px-5 pt-4 pb-3 border-b text-left shrink-0">
            <DialogTitle>Template Preview</DialogTitle>
            <DialogDescription>
              Full view — fits both portrait and landscape. Scroll if needed.
              Press ESC or click outside to close.
            </DialogDescription>
          </DialogHeader>
          {/* flex-1 + min-h-0 bounds this to the leftover panel height so it —
              and only it — owns the modal's scrolling */}
          <div className="flex-1 min-h-0 overflow-auto bg-zinc-100 rounded-b-lg">
            <div className="flex justify-center items-start p-4 sm:p-6">
              <div className="bg-white shadow-lg max-w-full">
                <TemplateFrame
                  html={html}
                  title="Template preview (fullscreen)"
                  /* A4 portrait is 210mm wide; give the frame that much so the
                     template lays out at its intended width instead of being
                     squeezed by the dialog and wrapping its headings */
                  className="block border-0 w-[210mm] max-w-full"
                />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const getInputField = ({
  type,
  control,
  errors,
  setValue,
  place,
  group,
  name,
  validation,
  ...props
}: any) => {
  const { className, ...restProps } = props;
  switch (type) {
    case "button":
      return (
        <Button type="button" {...props}>
          {props.value}
        </Button>
      );

    case "checkbox":
      return (
        <Controller
          control={control}
          name={name}
          render={({ field }) => (
            <div className="flex items-center gap-2">
              <Checkbox
                checked={field.value}
                label={props.label}
                onCheckedChange={(value: boolean) => {
                  if (value) {
                    field.onChange(true);
                  } else {
                    field.onChange(false);
                  }
                }}
              />
            </div>
          )}
        />
      );

    case "submit":
      return (
        <Button type="submit" {...props}>
          {props.value}
        </Button>
      );
    case "createable":
      return (
        <Controller
          name={name}
          control={control}
          rules={validation}
          render={({ field }) => (
            <>
              {props?.label && <FormLabel label={`${props.label} `} />}
              <CreatableSelect {...props} options={props?.option} {...field} />
            </>
          )}
        />
      );

    case "react-select":
      return (
        <Controller
          name={name}
          control={control}
          rules={validation}
          render={({ field }) => (
            <>
              {props?.label && <FormLabel label={`${props.label} `} />}
              <ReactSelect
                {...props}
                options={props?.option}
                {...field}
                isDisabled={props.disabled}
              />
              {name && errors[name] && (
                <ErrorLabel message={errors[name].message} />
              )}
            </>
          )}
        />
      );
    case "date":
      return (
        <Controller
          name={name}
          control={control}
          rules={validation}
          render={({ field }) => {
            return (
              <div id={props?.id}>
                {props.label && <FormLabel label={props.label} />}
                <Calendar22
                  onChange={field.onChange}
                  value={field.value}
                  id={props?.id}
                />
                {name && errors[name] && (
                  <ErrorLabel message={errors[name].message} />
                )}
              </div>
            );
          }}
        />
      );
    case "editor":
      return (
        <Controller
          name={name}
          control={control}
          rules={validation}
          render={({ field }) => (
            <>
              <TextEditor
                {...props}
                {...field}
                height={props.height || 400}
                name={name}
                initialValue={field.value}
                error={errors[name] && errors[name].message}
              />
              <EditorJSComponent
                {...props}
                {...field}
                placeholder={props.placeholder}
                height={props.height || 400}
              />
            </>
          )}
        />
      );

    case "editorjs":
      return (
        <Controller
          name={name}
          control={control}
          rules={validation}
          render={({ field }) => (
            <EditorJSField
              field={field}
              props={props}
              name={name}
              errors={errors}
            />
          )}
        />
      );

    case "component":
      return (
        <div className={className}>
          {props.label && <FormLabel label={props.label} />}
          <div className="w-full">{props.component}</div>
          {name && errors[name] && (
            <ErrorLabel message={errors[name].message} />
          )}
        </div>
      );

    case "async":
      return (
        <>
          <Controller
            name={name}
            control={control}
            rules={validation}
            render={({ field: { onChange, value, ...restFields } }) => (
              <div aria-disabled={props.disabled}>
                {props.label && <FormLabel label={props.label} />}
                <AsyncSelect
                  {...restFields}
                  {...props}
                  onChange={(e) => {
                    props?.onChange?.(e);

                    onChange(e);
                  }}
                  defaultValue={value}
                  format={props.format || null}
                  endpoint={props.endpoint || ""}
                  label={props?.label}
                  disabled={props.disabled}
                  error={errors[name]?.message}
                  className={props?.className}
                  settings={props?.settings}
                  defaultOptions={props?.option}
                />
                {name && errors[name] && (
                  <ErrorLabel
                    className="!mt-[-12px]"
                    message={errors[name].message}
                  />
                )}
              </div>
            )}
          />
        </>
      );
    case "code-mirror":
      // This is the Html editor for template
      return (
        <Controller
          name={name}
          control={control}
          rules={validation}
          render={({ field }) => (
            <CodeMirrorField
              field={field}
              props={props}
              name={name}
              errors={errors}
            />
          )}
        />
      );
    case "switch":
      return (
        <>
          <Controller
            name={name}
            control={control}
            rules={validation}
            render={({ field }) => (
              <>
                <div {...props}>
                  {" "}
                  <LabelSwitch
                    {...field}
                    {...restProps}
                    checked={field.value}
                    onClick={(e: any) => {
                      const dataState =
                        e.currentTarget.getAttribute("data-state");
                      if (dataState === "checked") {
                        setValue(name, false);
                      } else {
                        setValue(name, true);
                      }
                    }}
                    label={props.label}
                    error={errors[name] && errors[name].message}
                  />
                </div>
              </>
            )}
          />
        </>
      );
    case "prefixinput":
      return (
        <div className={className}>
          <Controller
            name={name}
            control={control}
            rules={validation}
            render={({ field }) => (
              <div {...props}>
                <PrefixInput
                  {...restProps}
                  {...field}
                  value={field?.value}
                  onChange={(e) => {
                    props?.onChange?.();
                    field?.onChange(e);
                  }}
                  type={props.prefixType}
                  placeholder={props.placeholder}
                  error={errors[name] && errors[name].message}
                />
              </div>
            )}
          />
        </div>
      );
    case "currencyinput":
      return (
        <div className={className}>
          <Controller
            name={name}
            control={control}
            rules={validation}
            render={({ field }) => (
              <div {...props}>
                <CurrencyInput
                  {...restProps}
                  {...field}
                  setValue={setValue}
                  type={type}
                  placeholder={props.placeholder}
                  error={errors[name] && errors[name].message}
                />
              </div>
            )}
          />
        </div>
      );
    case "tel":
      return (
        <Controller
          name={name}
          control={control}
          rules={{
            required: validation?.required ?? "Mobile number is required *",
            validate: (value) => {
              if (!value) return true;
              const phoneNumber = parsePhoneNumberFromString(`+${value}`);
              return phoneNumber && phoneNumber.isValid()
                ? true
                : "Invalid mobile number or country code *";
            },
          }}
          render={({ field }) => (
            <PhonePicker
              label={props.label}
              error={errors[name] && errors[name].message}
              {...field}
              {...props}
            />
          )}
        />
      );
    case "file":
      return (
        <div>
          <Controller
            name={name}
            control={control}
            rules={validation}
            render={({ field: { onChange, value, ...restField } }) => (
              <div {...props}>
                <FormLabel label={props.label} />
                <UploadToCloud
                  onChange={onChange}
                  value={value}
                  id={name}
                  {...restField}
                  {...props}
                />
                <span className="text-[12px] text-red-600">
                  {errors[name] && errors[name].message}
                </span>
              </div>
            )}
          />
        </div>
      );
    case "cropper":
      return (
        <div className={className}>
          <Controller
            name={name}
            control={control}
            rules={validation}
            render={({ field: { onChange, value, ...restField } }) => (
              <div className="my-[20px]">
                {props.label && <FormLabel label={props.label} />}

                <DndUpload
                  error={errors[name] && errors[name].message}
                  {...props}
                  {...restField}
                  isMulti={props.isMulti}
                  onChange={onChange}
                  value={value}
                  type={props.upload_type}
                />
                <span className="text-[12px] text-red-600">
                  {errors[name] && errors[name].message}
                </span>
              </div>
            )}
          />
        </div>
      );

    case "otp":
      return (
        <div className={className}>
          <Controller
            name={name}
            control={control}
            rules={validation}
            render={({ field }) => (
              <>
                <OTPInput
                  {...props}
                  {...field}
                  error={errors[name] && errors[name].message}
                />
              </>
            )}
          />
        </div>
      );
    case "fileupload":
      return (
        <div className={className}>
          <Controller
            name={name}
            control={control}
            rules={validation}
            render={({ field: { value, onChange, ...restFields } }) => (
              <>
                <FileUpload
                  allowCopy={props?.allowCopy}
                  multiple={props?.isMulti ?? false}
                  onChange={(e) => {
                    onChange(e);
                    props?.onChange?.();
                  }}
                  value={value}
                  {...props}
                  {...restFields}
                />
              </>
            )}
          />
        </div>
      );
    case "textarea":
      return (
        <Controller
          name={name}
          control={control}
          rules={validation}
          render={({ field }) => (
            <>
              {" "}
              {props.label && <FormLabel label={props.label} />}
              <textarea
                {...props}
                {...field}
                rows={5}
                className={cn(
                  errors[name]
                    ? "border border-red-500 rounded-md p-2"
                    : " rounded-md border p-2",
                  className,
                )}
              />
              <span className="text-[12px] block text-red-600">
                {errors[name] && errors[name].message}
              </span>
            </>
          )}
        />
      );
    default:
      return (
        <>
          <Controller
            name={name}
            control={control}
            rules={validation}
            render={({ field }) => (
              <div {...props}>
                {props.label && <FormLabel label={props.label} />}
                <Input
                  {...restProps}
                  {...field}
                  aria-invalid={
                    errors[name] && errors[name].message ? true : false
                  }
                  aria-disabled={props.disabled}
                  type={type}
                  placeholder={props.placeholder}
                  error={errors[name] && errors[name].message}
                />
              </div>
            )}
          />
        </>
      );
  }
};

function getUrlFileName(url: string) {
  const segments = url.split("/");
  return segments.pop();
}

function getTrim(text: string, length: number) {
  if (length > text.length) return text;
  text = text.trim().substring(0, length) + "...";
  return text;
}

const getExtension = (fileName: string) => {
  const parts = fileName?.split(".");
  return parts.length > 1 ? parts[parts.length - 1] : "";
};

const convetBase64Encoder = (data: string) => {
  let decodedStr = atob(data);
  let jsonData = JSON.parse(decodedStr || "");
  return jsonData;
};

const handleKeyDown = (event: React.KeyboardEvent) => {
  if (event.key === "Enter") {
    event.preventDefault();
  }
};

const useSlugs = (data = "") => {
  return data?.trim().toLowerCase().replace(/ /g, "-");
};

const useReverseSlug = (slug: any) => {
  return (
    slug &&
    slug
      ?.split("-")
      ?.map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
};

export {
  convetBase64Encoder,
  getExtension,
  getInputField,
  getTrim,
  getUrlFileName,
  handleKeyDown,
  useReverseSlug,
  useSlugs,
};
